import type { ConvertLeadResponse } from '@/services/api/MarketingApiTypes';
import type {
  AdditionalContact,
  MidpointIntakeRequest,
  MidpointIntakeResponse,
} from '@/types/forms';
import type { WizardState } from '../types';

/**
 * Convert lead to opportunity via existing API route.
 * Uses POST /api/finalize-lead which calls Marketing API convertLead.
 *
 * @param leadId - The Salesforce lead ID to convert
 * @returns Account, contact, and opportunity IDs from conversion
 */
export async function convertLead(
  leadId: string
): Promise<ConvertLeadResponse> {
  const response = await fetch('/api/finalize-lead', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ leadID: leadId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      `Failed to convert lead: ${error.error || response.statusText}`
    );
  }

  return response.json();
}

/**
 * Build the finalize opportunity payload from wizard state.
 * Matches MidpointIntakeRequest type expected by /api/finalize-opportunity.
 *
 * @param state - The wizard state containing form data
 * @param ids - The IDs from lead conversion
 * @returns Payload for finalizing the opportunity
 */
function buildFinalizePayload(
  state: WizardState,
  ids: ConvertLeadResponse
): MidpointIntakeRequest {
  const members = state.formData.members || [];
  const primaryMember = members.find(m => m.isPrimary);
  const otherMembers = members.filter(m => !m.isPrimary);

  const primaryContact: AdditionalContact = {
    FirstName: primaryMember?.first_name,
    LastName: primaryMember?.last_name,
    Birthdate: primaryMember?.date_of_birth || '',
    Membership_Type__c: primaryMember?.plan,
  };

  const additionalContacts: AdditionalContact[] = otherMembers.map(m => ({
    FirstName: m.first_name,
    LastName: m.last_name,
    Birthdate: m.date_of_birth,
    Membership_Type__c: m.plan,
    //Membership_Type__c: m.pediatrics_enabled ? 'Pediatrics' : undefined,
  }));

  return {
    opportunityId: ids.opportunityId,
    primaryContactId: ids.contactId,
    accountId: ids.accountId,
    primaryContact,
    additionalContacts,
    type: 'E-Commerce',
    promoCode: state.formData.discount_code,
  };
}

/**
 * Finalize opportunity to create invoice.
 *
 * @param state - The wizard state containing form data
 * @param ids - The IDs from lead conversion
 * @returns The invoice ID from the finalized opportunity
 */
export async function finalizeOpportunity(
  state: WizardState,
  ids: ConvertLeadResponse
): Promise<string> {
  const payload = buildFinalizePayload(state, ids);

  const response = await fetch('/api/finalize-opportunity', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      `Failed to finalize opportunity (${response.status}): ${
        error.detail || error.error || response.statusText
      }`
    );
  }

  const result: MidpointIntakeResponse = await response.json();
  const invoiceId = result.invoiceId;

  if (!invoiceId) {
    throw new Error('No invoiceId in API response');
  }

  return invoiceId;
}

async function resolveCheckoutIds(
  state: WizardState
): Promise<ConvertLeadResponse> {
  if (state.opportunityId && state.contactId && state.accountId) {
    return {
      opportunityId: state.opportunityId,
      contactId: state.contactId,
      accountId: state.accountId,
    };
  }

  if (!state.leadId) {
    throw new Error('No leadId - cannot proceed to checkout');
  }

  return convertLead(state.leadId);
}

export const initiateCheckout = async (state: WizardState): Promise<string> => {
  const ids = await resolveCheckoutIds(state);

  return finalizeOpportunity(state, ids);
};
